// Lógica PURA de la vista de todas las líneas (`LinesView`): qué filas se ven según el filtro de la
// URL, la búsqueda y el orden elegido. Sin React y sin i18n, igual que `panelFormatters` — se
// testea sin montar nada.
//
// Los valores de `?filtro=` son parte del contrato de la card gtAL5axI: son el DESTINO de los CTAs
// del Panel ("Definir objetivos" aterriza en `?filtro=sin-objetivo`, no frente a las 78 filas).
// Cambiarlos rompe esos deep links.
//
// `del-plan` lee `from_media_plan` y `inactivas` opera sobre `inactive_lines` — los dos llegaron
// al agregado del Panel en la tanda de paridad con Monitoreo (25/08). Con un backend viejo los
// campos no viajan y los chips quedan en (0), deshabilitados: degradan, no rompen.

import {SEVERITY_GOLD, SEVERITY_RED} from '../../agency/v2/clientsV2Thresholds';
import {alertLevelSeverity, spendPct} from './panelFormatters';

export const FILTER_OFF_PACE = 'fuera-de-ritmo';
export const FILTER_NO_BUDGET = 'sin-presupuesto';
export const FILTER_NO_GOAL = 'sin-objetivo';
export const FILTER_FROM_PLAN = 'del-plan';
// Especial: no filtra `lines` (las activas) — cambia la LISTA a `inactive_lines`, que viaja aparte
// para no contaminar los conteos de los KPIs y las cards. El caller lo resuelve.
export const FILTER_INACTIVE = 'inactivas';

// En orden de pantalla (el mismo del mockup).
export const LINE_FILTERS = [FILTER_OFF_PACE, FILTER_NO_BUDGET, FILTER_NO_GOAL,
    FILTER_FROM_PLAN, FILTER_INACTIVE];

// `?objetivo=<id>`: la vista aterriza con el modal de objetivo de esa línea abierto (card
// 6zUFUONB). Lo produce el CTA del Listado v2 y lo consume `LinesView`: el nombre del param vive
// ACÁ, una sola vez, para que un rename no los separe en silencio.
export const GOAL_PARAM = 'objetivo';

export function defineGoalPath(clientId, lineId) {
    return `/client/${clientId}/lines?filtro=${FILTER_NO_GOAL}&${GOAL_PARAM}=${lineId}`;
}

export const SORT_SEVERITY = 'severidad';
export const SORT_BUDGET = 'presupuesto';
export const SORT_SPEND = 'consumo';
export const SORT_NAME = 'nombre';
export const SORT_KEYS = [SORT_SEVERITY, SORT_BUDGET, SORT_SPEND, SORT_NAME];

export function lineHasBudget(line) {
    return Number(((line || {}).budget || {}).allocated) > 0;
}

export function lineHasGoal(line) {
    return !!((line || {}).goal || {}).defined;
}

// "Pide atención" = el veredicto de presupuesto de la agencia está en rojo u oro. Es el MISMO
// `budget_alert_level` que dispara las alertas (el backend ya lo manda en null si la evaluación no
// es fresca o está silenciada), no un umbral inventado acá.
export function lineNeedsAttention(line) {
    const severity = alertLevelSeverity((line || {}).budget_alert_level);
    return severity === SEVERITY_RED || severity === SEVERITY_GOLD;
}

const FILTER_PREDICATES = {
    [FILTER_OFF_PACE]: lineNeedsAttention,
    [FILTER_NO_BUDGET]: (line) => !lineHasBudget(line),
    [FILTER_NO_GOAL]: (line) => !lineHasGoal(line),
    // `=== true` a propósito: con un backend viejo el campo no viaja (undefined) y el chip tiene
    // que contar 0, no "todo lo que no diga false".
    [FILTER_FROM_PLAN]: (line) => (line || {}).from_media_plan === true,
};

/** `true` si `filter` es uno de los valores del contrato (un `?filtro=` desconocido se ignora). */
export function isKnownFilter(filter) {
    return !!FILTER_PREDICATES[filter] || filter === FILTER_INACTIVE;
}

/** Cuántas líneas matchean cada filtro — los contadores de los chips. */
export function filterCount(lines, filter) {
    const predicate = FILTER_PREDICATES[filter];
    if (!predicate) return 0;
    return (lines || []).filter(predicate).length;
}

// Los sorts con dirección propia. El orden por severidad NO está acá a propósito: es el orden en
// que el backend manda `lines` (CRITIC → BAD → sin dato → OK, la misma unidad de veredicto que
// dispara las alertas), y recalcularlo en el front sería inventar un segundo criterio que tarde o
// temprano contradice al primero (docstring de `ControlLinesCard`).
const SORT_COMPARATORS = {
    // Mayor presupuesto primero; sin presupuesto al final (no "en cero": no es lo mismo).
    [SORT_BUDGET]: (a, b) => descNullsLast(budgetOf(a), budgetOf(b)),
    // Mayor % consumido primero; sin porcentaje posible (sin presupuesto) al final.
    [SORT_SPEND]: (a, b) => descNullsLast(spendPctOf(a), spendPctOf(b)),
    [SORT_NAME]: (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es'),
};

function budgetOf(line) {
    return lineHasBudget(line) ? Number(line.budget.allocated) : null;
}

function spendPctOf(line) {
    const budget = (line || {}).budget || {};
    return spendPct(budget.spend, budget.allocated);
}

function descNullsLast(a, b) {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return b - a;
}

/**
 * Las filas visibles: filtro → búsqueda → orden. `lines` viene del agregado del Panel ya ordenado
 * por severidad; con `sort` en `severidad` (o desconocido) se devuelve en ESE orden. Los sorts son
 * estables, así que los empates conservan la severidad del backend.
 *
 * `keep` (opcional, un Set de ids): líneas que se muestran aunque ya no pasen el filtro ni la
 * búsqueda. Son las que el usuario acaba de editar desde esta vista (card RVO9leLh): con
 * `?filtro=sin-objetivo`, guardar el objetivo sacaba a la línea del filtro y la pantalla quedaba en
 * «Ninguna línea coincide», como si el guardado la hubiera borrado. Quedan en su lugar del orden.
 */
export function visibleLines(lines, {filter, query, sort, keep = null} = {}) {
    let out = [...(lines || [])];
    const kept = (line) => !!keep && keep.has(line.id);

    const predicate = FILTER_PREDICATES[filter];
    if (predicate) out = out.filter((line) => kept(line) || predicate(line));

    const term = String(query || '').trim().toLowerCase();
    if (term) {
        out = out.filter((line) => kept(line) || String(line.name || '').toLowerCase().includes(term));
    }

    const comparator = SORT_COMPARATORS[sort];
    if (comparator) out.sort(comparator);

    return out;
}
